/**
 * The paper that travels with the goods — one stock transfer as a print-ready
 * A5 portrait sheet.
 *
 * A transfer is the one inventory document that leaves the building: the boxes
 * go in a van and somebody at the other end has to agree that what is written
 * here is what arrived. So the sheet is built around that moment — the line
 * table carries ordered / shipped / received side by side, and the foot carries
 * a signature block, because the receiver's signature IS the evidence for the
 * shortfall the receive screen records.
 *
 * No app shell: a slim viewer chrome (back · print) over the paper, exactly like
 * JournalVoucherPrintPage, and `@page { size: A5 }` so Ctrl/⌘+P lands on the
 * right stock. Long transfers are allowed to run onto a second sheet rather than
 * being scaled down — a picking list that cannot be read at arm's length in a
 * warehouse is worse than a second page.
 */

import type { Item, StockTransfer, StockTransferStatus } from '@erp/shared';
import { ArrowLeft, Printer } from 'lucide-react';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { getBrand } from '../../lib/brandClient.js';
import { formatDate, formatDateTime, formatNumber } from '../../lib/intl.js';
import { PALETTES, loadPalette, swatchAccent } from '../../lib/palette.js';

/** Colour of the status chip on the paper — never the app's Badge tones, which
 *  are theme-aware and would print as a grey smudge in dark mode. */
const STATUS_CLASS: Record<StockTransferStatus, string> = {
  draft: 'draft',
  in_transit: 'transit',
  received: 'ok',
  cancelled: 'bad',
};

/** The subset of Warehouse this sheet prints. */
interface PaperWarehouse {
  id: string;
  code: string;
  name: string;
}

export function StockTransferPrintPage(): JSX.Element {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const brand = getBrand();

  const [tr, setTr] = useState<StockTransfer | null>(null);
  const [warehouses, setWarehouses] = useState<PaperWarehouse[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [tenant, setTenant] = useState<{ name: string } | null>(null);

  // Paper brand = the active palette's BASE primary (saturated; reads on white).
  const { paperBrand, paperAccent } = useMemo(() => {
    const p = PALETTES.find((x) => x.id === loadPalette()) ?? PALETTES[0]!;
    return { paperBrand: p.primary, paperAccent: swatchAccent(p) };
  }, []);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const r = await api().get<StockTransfer>(`/stock-transfers/${id}`).catch(() => null);
      if (!r) return;
      setTr(r.data);
      // Products are fetched BY ID, one request per distinct line, rather than
      // off a paged /items list: a catalogue past the first page would print an
      // id where the product name belongs, on the one document a warehouse man
      // has to match against physical boxes.
      const ids = [...new Set(r.data.lines.map((l) => l.itemId))];
      const fetched = await Promise.all(
        ids.map((iid) => api().get<Item>(`/items/${iid}`).then((x) => x.data).catch(() => null)),
      );
      setItems(fetched.filter((x): x is Item => x !== null));
      await api().get<{ items: PaperWarehouse[] }>('/warehouses')
        .then((w) => setWarehouses(w.data.items)).catch(() => setWarehouses([]));
      await api().get<{ name: string }>('/tenants/current')
        .then((tn) => setTenant({ name: tn.data.name })).catch(() => setTenant(null));
    })();
  }, [id]);

  const warehouse = useMemo(() => {
    const by = new Map(warehouses.map((w) => [w.id, w]));
    return (wid: string): PaperWarehouse | null => by.get(wid) ?? null;
  }, [warehouses]);

  const item = useMemo(() => {
    const by = new Map(items.map((i) => [i.id, i]));
    return (iid: string): Item | null => by.get(iid) ?? null;
  }, [items]);

  const locale = i18n.language;

  if (!tr) return <p className="p-6 text-sm text-fg-muted">{tc('actions.loading', { defaultValue: 'Loading…' })}</p>;

  // Latin digits are pinned: this sheet is read (and re-counted against) by
  // whoever unloads the van, so the quantities must not follow the staff user's
  // personal numerals preference.
  const n = (v: string | number): string => formatNumber(v, { locale, numberingSystem: 'latn' });
  const from = warehouse(tr.fromWarehouseId);
  const to = warehouse(tr.toWarehouseId);
  const sellerName = tenant?.name ?? brand.name.full;
  const statusLabel = t(`transfers.state.${tr.status}`, { defaultValue: tr.status });
  const title = t('transfers.print.title', { defaultValue: 'Stock transfer note' });
  // The line count and the total pieces are what gets checked against the van
  // before it leaves; both are on the paper so nobody has to add the column up.
  const totalQty = tr.lines.reduce((s, l) => s + Number(l.quantity), 0);

  const rootStyle = { '--paper-brand': paperBrand, '--paper-accent': paperAccent } as CSSProperties;

  return (
    <div className="stp" dir={i18n.dir(locale)} style={rootStyle}>
      <style>{CSS}</style>
      <style media="print">{'@page{size:A5;margin:10mm}'}</style>

      {/* viewer chrome */}
      <header className="stp-bar">
        <Link to={`/inventory/transfers/${tr.id}`} className="stp-back">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span>{tc('actions.back', { defaultValue: 'Back' })}</span>
        </Link>
        <span className="stp-title">{title} · <span className="font-mono">{tr.transferNumber}</span></span>
        <span className="stp-spacer" />
        <button type="button" className="stp-print" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden />
          <span>{t('transfers.print.action', { defaultValue: 'Print / PDF' })}</span>
        </button>
      </header>

      <div className="stp-stage">
        <div className="a5">
          <div className="stp-top">
            <div className="brand">
              <div className="mk" aria-hidden>{brand.name.short?.slice(0, 2) || 'IQ'}</div>
              <div className="co">{sellerName}</div>
            </div>
            <div className="doc">
              <div className="dt">{title}</div>
              <div className="dn">{tr.transferNumber}</div>
            </div>
          </div>

          <div className="stp-meta">
            <div className="mi">
              <div className="k">{t('transfers.print.date', { defaultValue: 'Date' })}</div>
              <div className="v">{formatDate(tr.createdAt, { locale })}</div>
            </div>
            <div className="mi">
              <div className="k">{t('transfers.print.linesCount', { defaultValue: 'Lines' })}</div>
              <div className="v">{n(tr.lines.length)}</div>
            </div>
            <div className="mi">
              <div className="k">{t('transfers.print.totalQty', { defaultValue: 'Total quantity' })}</div>
              <div className="v">{n(totalQty)}</div>
            </div>
            <span className={`chip ${STATUS_CLASS[tr.status]}`}>{statusLabel}</span>
          </div>

          <div className="stp-body">
            {/* From and To are the two facts the driver is handed the sheet for,
                so they get a box each rather than a line in the meta strip. */}
            <div className="route">
              <div className="wh">
                <div className="k">{t('transfers.from', { defaultValue: 'From warehouse' })}</div>
                <div className="v">{from?.name ?? '—'}</div>
                {from?.code && <div className="c">{from.code}</div>}
              </div>
              <div className="arrow" aria-hidden>→</div>
              <div className="wh">
                <div className="k">{t('transfers.to', { defaultValue: 'To warehouse' })}</div>
                <div className="v">{to?.name ?? '—'}</div>
                {to?.code && <div className="c">{to.code}</div>}
              </div>
            </div>

            <table className="lines">
              <thead>
                <tr>
                  <th className="idx">#</th>
                  <th className="l">{t('transfers.item', { defaultValue: 'Product' })}</th>
                  <th>{t('transfers.qty', { defaultValue: 'Qty' })}</th>
                  <th>{t('transfers.shipped', { defaultValue: 'Shipped' })}</th>
                  <th>{t('transfers.receivedQty', { defaultValue: 'Received' })}</th>
                </tr>
              </thead>
              <tbody>
                {tr.lines.map((l) => {
                  const it = item(l.itemId);
                  const short = l.shippedQuantity != null && l.receivedQuantity != null
                    && Number(l.receivedQuantity) < Number(l.shippedQuantity);
                  return (
                    <tr key={l.id}>
                      <td className="idx">{l.lineNo}</td>
                      <td className="l">
                        {/* First-strong base direction: an Arabic product name on an
                            English sheet (or the reverse) otherwise renders its words
                            back to front. */}
                        <span className="iname">{it?.name ?? t('transfers.print.unknownItem', { defaultValue: 'Unnamed product' })}</span>
                        {it?.code && <span className="icode">{it.code}{it.barcode ? ` · ${it.barcode}` : ''}</span>}
                      </td>
                      <td className="amt">{n(Number(l.quantity))}{it?.unitOfMeasure ? <span className="uom"> {it.unitOfMeasure}</span> : null}</td>
                      <td className={l.shippedQuantity != null ? 'amt' : 'muted'}>
                        {l.shippedQuantity != null ? n(Number(l.shippedQuantity)) : '—'}
                      </td>
                      <td className={l.receivedQuantity != null ? `amt${short ? ' short' : ''}` : 'muted'}>
                        {l.receivedQuantity != null ? n(Number(l.receivedQuantity)) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Every stamped moment the transfer has, so the sheet in the folder
                answers "when did this leave / land" without the app. */}
            <div className="stamps">
              <span>{t('transfers.created', { defaultValue: 'Created' })}: {formatDateTime(tr.createdAt, { locale })}</span>
              {tr.shippedAt && <span>{t('transfers.shippedAt', { defaultValue: 'Shipped' })}: {formatDateTime(tr.shippedAt, { locale })}</span>}
              {tr.receivedAt && <span>{t('transfers.receivedAt', { defaultValue: 'Received' })}: {formatDateTime(tr.receivedAt, { locale })}</span>}
            </div>

            <div className="notes">
              <div className="k">{t('transfers.print.notes', { defaultValue: 'Notes' })}</div>
              {/* The box is printed even with nothing in it: a transfer picks up
                  remarks on the road (a damaged box, a short pallet) and there
                  has to be somewhere to write them. */}
              <div className="v">{tr.notes ?? ''}</div>
            </div>
          </div>

          <div className="stp-foot">
            <div className="sigs">
              <div className="sig">
                <div className="line">{t('transfers.print.dispatchedBy', { defaultValue: 'Dispatched by' })}</div>
                <div className="hint">{t('transfers.print.nameSignDate', { defaultValue: 'Name · Signature · Date' })}</div>
              </div>
              <div className="sig">
                <div className="line">{t('transfers.print.carrier', { defaultValue: 'Driver / carrier' })}</div>
                <div className="hint">{t('transfers.print.nameSignDate', { defaultValue: 'Name · Signature · Date' })}</div>
              </div>
              <div className="sig">
                <div className="line">{t('transfers.print.receivedBy', { defaultValue: 'Received by' })}</div>
                <div className="hint">{t('transfers.print.nameSignDate', { defaultValue: 'Name · Signature · Date' })}</div>
              </div>
            </div>
            <div className="footline">
              <span>{sellerName}</span>
              <span>{t('transfers.print.goodsChecked', { defaultValue: 'Signed for as received in good condition unless noted above.' })}</span>
            </div>
            <span className="paper-strip" aria-hidden />
          </div>
        </div>
      </div>
    </div>
  );
}

/* Scoped paper styles. The paper itself is always white with dark text (a real
   printable document); only the brand band/accents use --paper-brand/-accent. */
const CSS = `
.stp { min-height: 100vh; display: flex; flex-direction: column; background: var(--color-bg-subtle); color: var(--color-fg); }
.stp-bar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  min-height: 56px; padding: 8px 20px; border-bottom: 1px solid var(--color-border); background: var(--color-bg); }
.stp-back { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 12px; border-radius: 8px;
  color: var(--color-fg-muted); font-size: 13px; font-weight: 600; border: 1px solid var(--color-border); background: var(--color-bg-subtle); }
.stp-back:hover { color: var(--color-fg); }
[dir="rtl"] .stp-back svg { transform: scaleX(-1); }
.stp-title { font-weight: 800; font-size: 14px; }
.stp-spacer { flex: 1; }
.stp-print { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 14px; border-radius: 8px;
  border: 0; background: var(--color-brand); color: var(--color-brand-contrast); font-size: 13px; font-weight: 700; cursor: pointer; }
.stp-stage { flex: 1; overflow: auto; padding: 32px 20px 64px; display: flex; flex-direction: column; align-items: center; }

/* A5 portrait: 148mm x 210mm ≈ 559px x 794px at 96dpi. */
.a5 { width: 559px; max-width: 100%; min-height: 794px; background: #fff; color: #1a2230;
  box-shadow: 0 30px 80px -20px rgba(0,0,0,.45), 0 8px 24px -8px rgba(0,0,0,.3); border-radius: 3px;
  display: flex; flex-direction: column; position: relative; }

.stp-top { padding: 16px 24px 14px; color: #fff; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  background: linear-gradient(135deg, var(--paper-brand), color-mix(in oklab, var(--paper-brand) 58%, #07071c));
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.stp-top .brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
.stp-top .mk { width: 38px; height: 38px; border-radius: 10px; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.24);
  display: grid; place-items: center; flex: none; font-weight: 800; font-size: 13px; letter-spacing: .04em; }
.stp-top .co { font-size: 16px; font-weight: 800; letter-spacing: -.02em; line-height: 1.2; }
.stp-top .doc { text-align: end; flex: none; }
.stp-top .doc .dt { font-size: 15px; font-weight: 800; }
.stp-top .doc .dn { font-size: 12px; opacity: .9; margin-top: 3px; font-variant-numeric: tabular-nums; direction: ltr; unicode-bidi: isolate; }

.stp-meta { display: flex; align-items: center; gap: 20px; padding: 10px 24px; border-bottom: 1px solid #ece8de; flex-wrap: wrap; }
.stp-meta .mi .k { font-size: 9px; color: #888FA5; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; }
[dir="rtl"] .stp-meta .mi .k { letter-spacing: 0; }
.stp-meta .mi .v { font-size: 12px; font-weight: 700; margin-top: 1px; color: #1a2230; font-variant-numeric: tabular-nums; }
.stp-meta .chip { margin-inline-start: auto; font-size: 10px; font-weight: 800; padding: 4px 10px; border-radius: 999px;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.stp-meta .chip.ok { background: #E4F4EC; color: #1F7A52; }
.stp-meta .chip.transit { background: #E6EEFC; color: #24468F; }
.stp-meta .chip.draft { background: #F1F1F4; color: #545B72; }
.stp-meta .chip.bad { background: #FBE7E5; color: #AE3A33; }

.stp-body { padding: 14px 24px 0; display: flex; flex-direction: column; flex: 1; }
.route { display: flex; align-items: stretch; gap: 10px; margin-bottom: 12px; }
.route .wh { flex: 1; padding: 8px 12px; background: #F4F6FB; border: 1px solid #ece8de; border-radius: 8px; min-width: 0; }
.route .wh .k { font-size: 9px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #888FA5; }
[dir="rtl"] .route .wh .k { letter-spacing: 0; }
.route .wh .v { font-size: 13px; font-weight: 800; margin-top: 2px; color: #1a2230; unicode-bidi: plaintext; }
.route .wh .c { font-size: 10px; color: #545B72; margin-top: 1px; direction: ltr; unicode-bidi: isolate; }
/* The arrow is a glyph, so it has to be mirrored by hand in RTL — the goods
   still travel from the start-side box to the end-side one. */
.route .arrow { align-self: center; font-size: 18px; color: var(--paper-brand); font-weight: 800; flex: none; }
[dir="rtl"] .route .arrow { transform: scaleX(-1); }

table.lines { width: 100%; border-collapse: collapse; }
.lines thead th { background: var(--paper-brand); color: #fff; font-size: 9.5px; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase; padding: 7px 9px; text-align: end; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
[dir="rtl"] .lines thead th { letter-spacing: 0; }
.lines thead th.l { text-align: start; } .lines thead th.idx { text-align: center; width: 26px; }
.lines thead th:first-child { border-start-start-radius: 8px; }
.lines thead th:last-child { border-start-end-radius: 8px; }
.lines tbody td { padding: 7px 9px; font-size: 11.5px; border-bottom: 1px solid #ece8de; text-align: end;
  font-variant-numeric: tabular-nums; break-inside: avoid; }
.lines tbody td.l { text-align: start; }
.lines tbody tr:nth-child(even) td { background: #F4F6FB; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lines td.idx { color: #888FA5; font-weight: 700; text-align: center; }
.lines td .iname { display: block; font-weight: 700; color: #1a2230; unicode-bidi: plaintext; }
/* A code read right to left is a different code. */
.lines td .icode { display: block; font-size: 9.5px; color: #888FA5; direction: ltr; unicode-bidi: isolate; }
.lines td .uom { font-size: 9.5px; font-weight: 600; color: #888FA5; }
.lines td.amt { font-weight: 700; color: #1a2230; }
/* Less arrived than left: the whole reason the receiver signs this sheet. */
.lines td.amt.short { color: #AE3A33; }
.lines td.muted { color: #C6CDDC; }

.stamps { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 8px; font-size: 9.5px; color: #545B72; }
.notes { margin-top: 10px; padding: 7px 12px; border: 1px solid #ece8de; border-radius: 8px; break-inside: avoid; }
.notes .k { font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #888FA5; }
[dir="rtl"] .notes .k { letter-spacing: 0; }
.notes .v { font-size: 11.5px; margin-top: 2px; color: #1a2230; line-height: 1.45; min-height: 26px; white-space: pre-wrap; }

.stp-foot { margin-top: auto; flex: none; padding: 14px 24px 0; break-inside: avoid; }
.sigs { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
.sigs .sig .line { border-top: 1.5px solid #C6CDDC; padding-top: 5px; margin-top: 34px; font-size: 10px; color: #1a2230; font-weight: 700; text-align: center; }
.sigs .sig .hint { font-size: 8.5px; color: #888FA5; text-align: center; margin-top: 1px; }
.footline { display: flex; justify-content: space-between; gap: 12px; padding: 9px 0; margin-top: 8px; font-size: 9px; color: #888FA5; border-top: 1px solid #ece8de; }
.paper-strip { display: block; height: 6px; margin: 0 -24px; background: linear-gradient(90deg, var(--paper-brand), var(--paper-accent));
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }

@media print {
  .stp { background: #fff; }
  .stp-bar { display: none !important; }
  .stp-stage { padding: 0; overflow: visible; display: block; }
  /* The @page margin is the whitespace now, and a fixed height would push a
     blank trailing sheet out of a long transfer. */
  .a5 { box-shadow: none; border-radius: 0; width: 100%; max-width: none; min-height: 0; display: block; }
  .lines thead { display: table-header-group; }
}
`;
